import { useEffect, useMemo, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    Eraser,
    KeyRound,
    Loader2,
    LogOut,
    Mail,
    MessagesSquare,
    RefreshCw,
    Search,
    ShieldCheck,
    ShieldQuestion,
    ShieldX,
    Smartphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMobileDeviceCommand, useMobileDevices, useTenantUsers } from "@/api/hooks";
import type { MobileDevice, MobileDeviceCommand, TenantUser } from "@/api/adminBff";
import {
    APP_LABELS,
    normaliseAttestation,
    normaliseDeviceStatus,
    osLabel,
    platformLabel,
    relativeTime,
    type AttestationKey,
    type DeviceStatusKey
} from "./mobilePolicy";
import type { Notify } from "./Toast";

const PAGE_SIZE = 50;

/** Values are Identity's own status spellings (contract §4 Admin) — sent as `status=`; "" = any. */
type StatusFilter = "" | "active" | "wipe_pending" | "revoked" | "wiped";
const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
    { value: "", label: "Any status" },
    { value: "active", label: "Active" },
    { value: "wipe_pending", label: "Wipe pending" },
    { value: "revoked", label: "Revoked" },
    { value: "wiped", label: "Wiped" }
];

const STATUS_STYLE: Record<DeviceStatusKey, { label: string; cls: string }> = {
    active: { label: "Active", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
    "wipe-pending": { label: "Wipe pending", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
    revoked: { label: "Revoked", cls: "bg-muted text-muted-foreground" },
    wiped: { label: "Wiped", cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200" },
    unknown: { label: "Unknown", cls: "bg-muted text-muted-foreground" }
};

const ATTESTATION_STYLE: Record<AttestationKey, { label: string; cls: string; icon: typeof ShieldCheck; hint: string }> = {
    verified: {
        label: "Verified",
        cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
        icon: ShieldCheck,
        hint: "Apple / Google confirmed this is the genuine Athavita app on real hardware."
    },
    unverified: {
        label: "Unverified",
        cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
        icon: ShieldQuestion,
        hint: "No hardware attestation was available (e.g. simulator, de-Googled phone or attestation turned off)."
    },
    failed: {
        label: "Failed",
        cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
        icon: ShieldX,
        hint: "Attestation was checked and did not pass — the app or device may be modified."
    },
    unknown: {
        label: "—",
        cls: "bg-muted text-muted-foreground",
        icon: ShieldQuestion,
        hint: "Not reported."
    }
};

const APP_ICONS: Record<string, typeof Mail> = {
    mail: Mail,
    connect: MessagesSquare,
    authenticator: KeyRound
};

interface Props {
    /** null = all tenants (SuperAdmin). */
    tenantId: string | null;
    tenantNames: Map<string, string>;
    notify: Notify;
}

export function MobileDevicesTab({ tenantId, tenantNames, notify }: Props) {
    const [skip, setSkip] = useState(0);
    const [userId, setUserId] = useState("");
    const [status, setStatus] = useState<StatusFilter>("");
    const [query, setQuery] = useState("");
    const [pending, setPending] = useState<{ device: MobileDevice; command: MobileDeviceCommand } | null>(null);
    const [commandError, setCommandError] = useState<string | null>(null);

    // A new tenant, user or status filter starts again from the first page.
    useEffect(() => {
        setSkip(0);
        setUserId("");
    }, [tenantId]);
    useEffect(() => setSkip(0), [userId, status]);

    const users = useTenantUsers(tenantId);
    // User and status are filtered by Identity, so paging and the total count reflect them. The search box
    // below only narrows the page already on screen.
    const devices = useMobileDevices({
        tenantId,
        userId: userId || null,
        status: status || null,
        skip,
        take: PAGE_SIZE
    });
    const command = useMobileDeviceCommand();

    const usersById = useMemo(
        () => new Map((users.data ?? []).map((u) => [u.id, u] as const)),
        [users.data]
    );

    const items = useMemo(() => devices.data?.items ?? [], [devices.data]);
    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((d) => {
            const u = d.userId ? usersById.get(d.userId) : undefined;
            return [d.userEmail, d.userDisplayName, u?.email, u?.displayName, d.name, d.model, d.deviceId]
                .some((v) => v?.toLowerCase().includes(q));
        });
    }, [items, query, usersById]);

    const total = devices.data?.total ?? null;
    const hasNext = total !== null ? skip + PAGE_SIZE < total : items.length === PAGE_SIZE;
    const showTenantColumn = tenantId === null;

    const runCommand = () => {
        if (!pending) return;
        const { device, command: cmd } = pending;
        const label = deviceLabel(device);
        setCommandError(null);
        command.mutate(
            { deviceId: device.deviceId, command: cmd, tenantId: device.tenantId ?? tenantId },
            {
                onSuccess: () => {
                    setPending(null);
                    notify(
                        "success",
                        cmd === "wipe"
                            ? `Wipe queued for ${label}. It shows "Wipe pending" until the device checks in and deletes its data.`
                            : `${label} was signed out. Its session no longer works.`
                    );
                },
                onError: (e) => setCommandError(e.message)
            }
        );
    };

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search
                        className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                    />
                    <Input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter this page by user, device or model…"
                        aria-label="Filter devices on this page"
                        className="w-72 pl-7"
                    />
                </div>
                {tenantId ? (
                    <select
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        aria-label="Filter by user"
                        className="max-w-[16rem] rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="">All users</option>
                        {[...(users.data ?? [])]
                            .sort((a, b) => a.email.localeCompare(b.email))
                            .map((u) => (
                                <option key={u.id} value={u.id}>{u.displayName ? `${u.displayName} — ${u.email}` : u.email}</option>
                            ))}
                    </select>
                ) : null}
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as StatusFilter)}
                    aria-label="Filter by status"
                    className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                    {STATUS_FILTERS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>
                <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    onClick={() => void devices.refetch()}
                    disabled={devices.isFetching}
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${devices.isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {devices.isPending ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading devices…
                </div>
            ) : devices.isError ? (
                <div className="rounded-lg border border-destructive/40 bg-card p-4 text-sm">
                    <p className="font-medium text-destructive">Couldn&apos;t load devices.</p>
                    <p className="mt-1 text-muted-foreground">{devices.error.message}</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    <Smartphone className="mx-auto mb-2 h-6 w-6" aria-hidden />
                    {items.length > 0
                        ? "No devices on this page match the search."
                        : status || userId
                          ? "No devices match these filters."
                          : "No phones have registered yet. A device appears here after a user signs in to an Athavita mobile app."}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border bg-card shadow-sm">
                    <table className="w-full min-w-[60rem] text-sm">
                        <thead className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                            <tr>
                                <th scope="col" className="px-3 py-2">User</th>
                                {showTenantColumn ? <th scope="col" className="px-3 py-2">Tenant</th> : null}
                                <th scope="col" className="px-3 py-2">App</th>
                                <th scope="col" className="px-3 py-2">Device</th>
                                <th scope="col" className="px-3 py-2">OS / app version</th>
                                <th scope="col" className="px-3 py-2">Attestation</th>
                                <th scope="col" className="px-3 py-2">Status</th>
                                <th scope="col" className="px-3 py-2">Last seen</th>
                                <th scope="col" className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {rows.map((d) => (
                                <DeviceRow
                                    key={d.deviceId}
                                    device={d}
                                    user={d.userId ? usersById.get(d.userId) : undefined}
                                    tenantName={showTenantColumn ? (d.tenantId ? tenantNames.get(d.tenantId) ?? d.tenantId : "—") : null}
                                    onCommand={(cmd) => {
                                        setCommandError(null);
                                        setPending({ device: d, command: cmd });
                                    }}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {devices.data && (skip > 0 || hasNext) ? (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                    <span>
                        {items.length > 0 ? `${skip + 1}–${skip + items.length}` : "0"}
                        {total !== null ? ` of ${total}` : ""}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                        disabled={skip === 0 || devices.isFetching}
                        aria-label="Previous page"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSkip(skip + PAGE_SIZE)}
                        disabled={!hasNext || devices.isFetching}
                        aria-label="Next page"
                    >
                        <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                </div>
            ) : null}

            {pending ? (
                <ConfirmCommandDialog
                    device={pending.device}
                    command={pending.command}
                    userLabel={userLabel(pending.device, pending.device.userId ? usersById.get(pending.device.userId) : undefined)}
                    pending={command.isPending}
                    error={commandError}
                    onConfirm={runCommand}
                    onClose={() => setPending(null)}
                />
            ) : null}
        </div>
    );
}

function deviceLabel(d: MobileDevice): string {
    return d.name || d.model || "this device";
}

function userLabel(d: MobileDevice, u: TenantUser | undefined): string {
    return d.userEmail ?? u?.email ?? d.userDisplayName ?? u?.displayName ?? d.userId ?? "unknown user";
}

function DeviceRow({
    device: d,
    user,
    tenantName,
    onCommand
}: {
    device: MobileDevice;
    user: TenantUser | undefined;
    tenantName: string | null;
    onCommand: (command: MobileDeviceCommand) => void;
}) {
    const status = normaliseDeviceStatus(d.status);
    const statusStyle = STATUS_STYLE[status];
    const os = osLabel(d.platform, d.osVersion);
    const att = ATTESTATION_STYLE[normaliseAttestation(d.attestationStatus)];
    const appKey = (d.app ?? "").toLowerCase();
    const AppIcon = APP_ICONS[appKey] ?? Smartphone;
    const name = d.userDisplayName ?? user?.displayName ?? null;
    const email = d.userEmail ?? user?.email ?? d.userId ?? "—";
    const signedOut = status === "revoked" || status === "wiped" || status === "wipe-pending";
    const wipeDone = status === "wiped" || status === "wipe-pending";

    return (
        <tr className="align-top hover:bg-muted/30">
            <td className="px-3 py-2">
                <div className="font-medium">{name ?? email}</div>
                {name ? <div className="text-xs text-muted-foreground">{email}</div> : null}
            </td>
            {tenantName !== null ? <td className="px-3 py-2 text-xs">{tenantName}</td> : null}
            <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1.5">
                    <AppIcon className="h-3.5 w-3.5 text-primary" aria-hidden />
                    {APP_LABELS[appKey] ?? d.app ?? "—"}
                </span>
            </td>
            <td className="px-3 py-2">
                <div>{d.name || "Unnamed device"}</div>
                <div className="text-xs text-muted-foreground">
                    {platformLabel(d.platform)}
                    {d.model ? ` · ${d.model}` : ""}
                </div>
            </td>
            <td className="px-3 py-2 text-xs">
                <div title={os.hint}>{os.text}</div>
                <div className="text-muted-foreground">App {d.appVersion ?? "—"}</div>
            </td>
            <td className="px-3 py-2">
                <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${att.cls}`}
                    title={d.attestationReason ? `${att.hint} Reason: ${d.attestationReason}` : att.hint}
                >
                    <att.icon className="h-3 w-3" aria-hidden />
                    {att.label}
                </span>
            </td>
            <td className="px-3 py-2">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusStyle.cls}`}>
                    {status === "unknown" ? d.status ?? "Unknown" : statusStyle.label}
                </span>
            </td>
            <td className="px-3 py-2 text-xs">
                <div title={d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : undefined}>
                    {relativeTime(d.lastSeenAt)}
                </div>
                {d.createdAt ? (
                    <div className="text-muted-foreground">Registered {new Date(d.createdAt).toLocaleDateString()}</div>
                ) : null}
            </td>
            <td className="px-3 py-2">
                <div className="flex justify-end gap-1">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCommand("revoke")}
                        disabled={signedOut}
                        title={signedOut ? "Already signed out" : "Sign this device out"}
                    >
                        <LogOut className="h-3.5 w-3.5" /> Revoke
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive"
                        onClick={() => onCommand("wipe")}
                        disabled={wipeDone}
                        title={wipeDone ? "Wipe already requested" : "Delete Athavita data from this device"}
                    >
                        <Eraser className="h-3.5 w-3.5" /> Wipe
                    </Button>
                </div>
            </td>
        </tr>
    );
}

function ConfirmCommandDialog({
    device,
    command,
    userLabel: who,
    pending,
    error,
    onConfirm,
    onClose
}: {
    device: MobileDevice;
    command: MobileDeviceCommand;
    userLabel: string;
    pending: boolean;
    error: string | null;
    onConfirm: () => void;
    onClose: () => void;
}) {
    const [acknowledged, setAcknowledged] = useState(false);
    const isWipe = command === "wipe";
    const label = deviceLabel(device);
    const app = APP_LABELS[(device.app ?? "").toLowerCase()] ?? device.app ?? "the Athavita app";

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !pending) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, pending]);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-confirm-title"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={pending ? undefined : onClose}
        >
            <div
                className="w-full max-w-lg rounded-lg border bg-background p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="mobile-confirm-title" className="text-base font-semibold">
                    {isWipe ? `Wipe Athavita data from ${label}?` : `Sign out ${label}?`}
                </h2>
                <p className="mb-3 text-xs text-muted-foreground">
                    {app} on {platformLabel(device.platform)}{device.model ? ` (${device.model})` : ""} · {who}
                </p>

                {isWipe ? (
                    <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                        <li>
                            <strong className="text-foreground">
                                Deletes all Athavita data on this device the next time it connects
                            </strong>{" "}
                            — cached mail and chats, attachments, drafts, the offline search index and the app&apos;s
                            keys — then shows the signed-out screen.
                        </li>
                        <li>
                            A silent push asks the device to check in straight away. If it is offline or switched off,
                            the wipe runs as soon as it next comes online; until then it shows as <em>Wipe pending</em>.
                        </li>
                        <li>
                            The session is revoked now, so the device can&apos;t download anything new even before the
                            wipe completes.
                        </li>
                        <li>
                            Only Athavita&apos;s app data is removed. The user&apos;s personal apps, photos and files are
                            not touched — this is not a factory reset.
                        </li>
                        <li>This can&apos;t be undone. The user can sign in again later, which registers a new device.</li>
                    </ul>
                ) : (
                    <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                        <li>
                            <strong className="text-foreground">Signs this device out now.</strong> Its session is
                            revoked, so every access and refresh token issued to it stops working and it can no longer
                            reach mail, chat or files.
                        </li>
                        <li>
                            The next time the app connects it is told it has been signed out: it drops its tokens and
                            returns to the sign-in screen.
                        </li>
                        <li>
                            For a lost phone or a departing employee, use <strong className="text-foreground">Wipe</strong>{" "}
                            instead — it also deletes the Athavita data stored on the phone.
                        </li>
                        <li>
                            The user can sign in again, which registers the phone as a new device. To prevent that,
                            disable the user&apos;s account.
                        </li>
                    </ul>
                )}

                {isWipe ? (
                    <label className="mt-4 flex items-start gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                            className="mt-0.5 h-4 w-4"
                        />
                        I understand this permanently deletes the Athavita data on this device.
                    </label>
                ) : null}

                {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>
                        Cancel
                    </Button>
                    <Button
                        variant={isWipe ? "destructive" : "default"}
                        size="sm"
                        onClick={onConfirm}
                        disabled={pending || (isWipe && !acknowledged)}
                    >
                        {pending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : isWipe ? (
                            <Eraser className="h-3.5 w-3.5" />
                        ) : (
                            <LogOut className="h-3.5 w-3.5" />
                        )}
                        {isWipe ? "Wipe device data" : "Sign out device"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
